import { BriefApplicationResponseDto } from '../general-types/briefs.dto';
import { FilterStatsDto } from '../general-types/filters.dto';
import { PieceHireQuote } from '../general-types/piece-hire-quote.dto';
import { SocialPlacementChannel } from './piece-list';
import { PLACEMENT_CHANNEL_LABEL } from './piece-rules';

/**
 * Display rules for a server piece-hire quote.
 *
 * The figures come from the API; this only decides how they read. Amounts stay in
 * pence until `toMajor`, so nothing here can round a price differently from the hire.
 */

/** Shown wherever a hire would owe nothing (F2). */
export const NOTHING_OWED_COPY = 'Nothing left to hire. Keep at least one placement';

/** Pence to the major units `CurrencyPipe` formats. */
export function toMajor(pence: number | null | undefined): number {
  return (pence ?? 0) / 100;
}

const CONTENT_LABEL: Record<PieceHireQuote['pieces'][number]['content'], string> = {
  ugc: 'UGC',
  advertising: 'Ad licence',
  social: 'Social post',
};

/** `Video 1 · 30s · UGC`. */
export function quotePieceLabel(line: PieceHireQuote['pieces'][number]): string {
  return `${line.label} · ${line.durationSeconds}s · ${CONTENT_LABEL[line.content]}`;
}

/** `YouTube Shorts audience · Video 1`. */
export function quoteAudienceLabel(line: PieceHireQuote['audience'][number]): string {
  return `${PLACEMENT_CHANNEL_LABEL[line.channel]} audience · ${line.pieceLabel}`;
}

/** What the brand pays for one channel's audience fees on this quote, in pence. */
export function channelAudienceBrand(quote: PieceHireQuote, channel: SocialPlacementChannel): number {
  return quote.audience
    .filter((line) => line.channel === channel)
    .reduce((total, line) => total + line.brand, 0);
}

/**
 * Which hire button a piece quote allows.
 *
 * `nothing-owed` blocks the hire. `delivery-only` is a hire with no network accepted,
 * which pays for the UGC videos alone and must not read as "Hire on 0 networks".
 */
export type PieceHireAction = 'nothing-owed' | 'delivery-only' | 'networks';

export function pieceHireAction(quote: Pick<PieceHireQuote, 'owed' | 'acceptedChannels'>): PieceHireAction {
  if (quote.owed.length === 0) {
    return 'nothing-owed';
  }
  return quote.acceptedChannels.length === 0 ? 'delivery-only' : 'networks';
}

/** Tab counts after one Applied creator is hired, so the tabs move without a refetch. */
export function filterStatsAfterHire(stats: FilterStatsDto): FilterStatsDto {
  return {
    ...stats,
    Applied: Math.max(0, (stats.Applied ?? 0) - 1),
    Hired: (stats.Hired ?? 0) + 1,
  };
}

/**
 * One applicant row after a hire, with what the server committed.
 *
 * The hire endpoint returns the bare application, without the creator or the
 * per-channel post state the list loaded, so only the hire's own fields are taken
 * from it.
 */
export function mergeHiredApplication(
  row: BriefApplicationResponseDto,
  hired: BriefApplicationResponseDto,
  fallbackAccepted: number[] | undefined,
): BriefApplicationResponseDto {
  return {
    ...row,
    applicationStatus: hired.applicationStatus ?? row.applicationStatus,
    applicationStatusModifiedDate: hired.applicationStatusModifiedDate ?? row.applicationStatusModifiedDate,
    hiredEarnings: hired.hiredEarnings ?? row.hiredEarnings,
    hiredBriefPricing: hired.hiredBriefPricing ?? row.hiredBriefPricing,
    // Prefer what the server committed over the local draft: it sanitises the list.
    crossPlatformsAccepted: hired.crossPlatformsAccepted ?? fallbackAccepted ?? [],
    hireRecord: hired.hireRecord ?? row.hireRecord,
  };
}

/**
 * The brand total reserved on a piece hire, VAT included, in pence; null before hire.
 * Hires from before the quote engine have no `quote`, but their `frozenAmount` is the
 * same reserved figure.
 */
export function hiredBrandTotal(application: Pick<BriefApplicationResponseDto, 'hireRecord'>): number | null {
  const record = application.hireRecord;
  return record?.quote?.brandTotal ?? record?.frozenAmount ?? null;
}
